export interface SignContent {
  problemaTitle: string
  problemaSubtitle: string
  problemas: string[]
  nucleoTitle: string
  nucleoDescription: string
  descubriras: string[]
  previewNote: string
  patronTitle: string
  patronSubtitle: string
  patronText: string
  patronKeywords: string[]
  desbloqueoItems: string[]
  paraQuienItems: string[]
}

export interface Sign {
  id: string
  name: string
  entidad: string
  arquetipo: string
  heroPromise: string
  colors: {
    primary: string
    secondary: string
    accent: string
  }
  particleType:
    | 'fire'
    | 'earth'
    | 'air'
    | 'water'
    | 'electric'
    | 'smoke'
    | 'light'
    | 'cosmic'
  phrase: string
  symbol: string
  imageUrl: string
  checkoutUrl: string
  checkoutUrlCompleta: string
  checkoutUrlUpsell: string
  transitionGif?: string
  content: SignContent
}

const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files'

const BASE_PARA_QUIEN = [
  'Sienten que ciertos patrones se repiten constantemente',
  'Buscan comprenderse desde una perspectiva más profunda',
  'Conectan con simbolismo, identidad y exploración emocional',
  'Desean una experiencia más inmersiva que una lectura tradicional',
  'Sienten curiosidad por descubrir nuevas formas de verse a sí mismos',
  'Buscan una experiencia digital más emocional y personal',
]

export const signs: Sign[] = [
  {
    id: 'aries',
    name: 'Aries',
    entidad: 'Carnero mitológico de fuego',
    arquetipo: 'El Portador del Fuego Primordial',
    heroPromise:
      'Tu fuego no es impulsividad. Es una fuerza que aún no aprendiste a dominar.',
    colors: {
      primary: '#C41E3A',
      secondary: '#FF6B35',
      accent: '#FF8C42',
    },
    particleType: 'fire',
    phrase: 'Algunos esperan el momento. Aries lo crea.',
    symbol: '♈',
    imageUrl: `${CDN_BASE}/01_ARIES_1.png?v=1778615905`,
    checkoutUrl: 'https://los12reinos.com/cart/42759429554270:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774841688158:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811164885086:1',
    transitionGif: `${CDN_BASE}/Create_an_ultra_cinematic_transition_animatio.gif?v=1778622164`,
    content: {
      problemaTitle: 'Algo dentro de ti se enciende antes que tú.',
      problemaSubtitle:
        'Y a veces ese fuego te consume antes de saber qué quieres hacer con él.',
      problemas: [
        'Empiezas con fuego pero te cuesta sostener lo que comienzas.',
        'Tu impaciencia te hace saltar al siguiente desafío antes de completar el anterior.',
        'Reaccionas antes de pensar y luego lidias con las consecuencias.',
        'Tu necesidad de ser primero a veces te deja solo en la cima.',
      ],
      nucleoTitle: 'El Núcleo Aries',
      nucleoDescription:
        'Una exploración de la naturaleza del Portador del Fuego Primordial. No es un plan de acción. Es una comprensión de por qué tu impulso no es imprudencia, sino tu forma de crear realidad. Por qué tu impaciencia es en realidad hambre de vida. Por qué tu verdadero poder no está en ganar, sino en iniciar.',
      descubriras: [
        'Por qué necesitas crear constantemente y qué se enciende dentro de ti cuando lo haces',
        'La diferencia entre tu impulso vital y la reacción automática que te confunde con impulsividad',
        'El patrón de inicio y abandono que repites, y de dónde nace realmente',
        'Cómo tu rabia esconde una emoción más profunda que aún no has nombrado',
        'Cómo amas cuando dejas de competir incluso con quienes te aman',
        'Cómo integrar tu fuego sin quemar lo que estás construyendo',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Aries.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Hay una intensidad dentro de ti que reacciona antes de pensar. Una urgencia por moverte, por empezar, por crear. No es impulsividad — es un patrón que aún no aprendiste a leer.',
      patronKeywords: ['impulsividad', 'intensidad', 'reacción', 'movimiento'],
      desbloqueoItems: [
        'Comprender por qué reaccionas con tanta intensidad',
        'Identificar patrones de impulso que suelen repetirse',
        'Explorar partes de tu fuego que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de acción',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'tauro',
    name: 'Tauro',
    entidad: 'Toro celestial de piedra y oro',
    arquetipo: 'El Guardián de la Tierra Sagrada',
    heroPromise:
      'No te aferras por capricho. Te aferras porque algo dentro de ti todavía teme perderlo todo.',
    colors: {
      primary: '#4A7C3A',
      secondary: '#8B6914',
      accent: '#D4AF37',
    },
    particleType: 'earth',
    phrase: 'Su fuerza no hace ruido. Se impone.',
    symbol: '♉',
    imageUrl: `${CDN_BASE}/02_TAURO_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759431913566:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774841720926:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811164983390:1',
    content: {
      problemaTitle:
        'Necesitas raíces. Pero a veces esas raíces se vuelven cadenas.',
      problemaSubtitle:
        'Lo que te dio paz también es lo que te mantiene atrapado donde ya no creces.',
      problemas: [
        'Te cuesta soltar personas, rutinas y emociones incluso cuando sabes que ya terminaron.',
        'Tu necesidad de seguridad puede convertirse en resistencia al cambio.',
        'Proteges tanto tu paz que a veces levantas muros imposibles de atravesar.',
        'Confundes estabilidad con quedarte donde ya no creces.',
      ],
      nucleoTitle: 'El Núcleo Tauro',
      nucleoDescription:
        'Una exploración de la naturaleza del Guardián de la Tierra Sagrada. No es sobre terquedad. Es sobre comprender por qué necesitas raíces profundas, por qué valoras tanto la estabilidad y cómo tu energía encuentra poder cuando aprende a evolucionar sin perder su esencia.',
      descubriras: [
        'Por qué necesitas sentir estabilidad emocional para permitirte avanzar',
        'Tu relación con el pleasure, la seguridad y lo que llamas "amor"',
        'El miedo silencioso a perder lo que ya construiste y cómo te paraliza',
        'Tu lado sombrío: posesividad, control emocional disfrazado de cuidado',
        'Cómo amas cuando bajas la armadura de tu propio cuerpo',
        'Cómo construir sin convertirte en piedra',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Tauro.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Te aferras a lo conocido porque el cambio se siente como perder el suelo. Construyes muros para proteger tu paz, pero a veces esos muros no te dejan avanzar. No es terquedad — es un patrón de preservación.',
      patronKeywords: ['resistencia', 'apego', 'estabilidad', 'control'],
      desbloqueoItems: [
        'Comprender por qué te resistes tanto a soltar',
        'Identificar patrones de apego que suelen repetirse',
        'Explorar partes de tu necesidad de control que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de tierra',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'geminis',
    name: 'Géminis',
    entidad: 'Criatura dual — dos aves/serpientes gemelas astrales',
    arquetipo: 'El Mensajero de los Dos Mundos',
    heroPromise:
      'No estás disperso. Vives en demasiadas versiones de ti mismo al mismo tiempo.',
    colors: {
      primary: '#E6C200',
      secondary: '#1E3A8A',
      accent: '#C0C0C0',
    },
    particleType: 'light',
    phrase: 'Donde otros escuchan una voz, Géminis escucha universos.',
    symbol: '♊',
    imageUrl: `${CDN_BASE}/03_GEMINIS_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759432241246:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774843195486:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811165147230:1',
    content: {
      problemaTitle: 'Tu mente nunca descansa.',
      problemaSubtitle:
        'Y a veces hablas tanto, piensas tanto, que dejas de escuchar lo que realmente sientes.',
      problemas: [
        'Saltas entre posibilidades, conversaciones y versiones de ti mismo.',
        'Te adaptas tan rápido que a veces ni tú sabes cuál es tu verdadera voz.',
        'Tu ansiedad mental te impide quedarte el tiempo suficiente con lo que duele.',
        'Te escondes detrás de palabras cuando lo que sientes es demasiado profundo.',
      ],
      nucleoTitle: 'El Núcleo Géminis',
      nucleoDescription:
        'Una exploración del Mensajero de los Dos Mundos. No es sobre indecisión. Es sobre comprender tu necesidad de movimiento mental, tu búsqueda constante de significado y la dualidad que vive dentro de ti.',
      descubriras: [
        'Por qué tu mente no se apaga aunque tu cuerpo esté agotado',
        'Las versiones de ti que activas con cada persona y cuál es la real',
        'Cómo la palabra se vuelve refugio cuando el sentimiento es demasiado',
        'Tu lado sombrío: dispersión, ansiedad y evasión disfrazadas de curiosidad',
        'Cómo amas cuando dejas de esconderte detrás de tus propias palabras',
        'Cómo integrar mente y emoción sin sentir que pierdes una de ellas',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Géminis.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Tu mente salta de un pensamiento a otro para no tener que quedarse a sentir lo que duele. Hablas, analizas, te adaptas. No es dispersión — es un patrón de evasión que disfraza tu profundidad.',
      patronKeywords: ['dispersión', 'ruido mental', 'evasión', 'dualidad'],
      desbloqueoItems: [
        'Comprender por qué tu mente nunca se detiene',
        'Identificar patrones de evasión que suelen repetirse',
        'Explorar partes de tu dualidad que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de aire',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'cancer',
    name: 'Cáncer',
    entidad: 'Cangrejo lunar mitológico',
    arquetipo: 'El Guardián de las Mareas Internas',
    heroPromise:
      'No exageras. Sientes a un volumen que el mundo nunca aprendió a escuchar.',
    colors: {
      primary: '#9CB4D8',
      secondary: '#1E3A8A',
      accent: '#F8F8FF',
    },
    particleType: 'water',
    phrase: 'Protege lo que ama como si fuera un reino.',
    symbol: '♋',
    imageUrl: `${CDN_BASE}/04_CANCER_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759432536158:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774843261022:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811165278302:1',
    content: {
      problemaTitle: 'Sientes demasiado, todo el tiempo.',
      problemaSubtitle:
        'Y aprendiste a cuidarte escondiéndote dentro de tu propio caparazón.',
      problemas: [
        'Recuerdas heridas que otros ya olvidaron.',
        'Das amor con intensidad, pero también temes ser lastimado.',
        'Tu memoria emocional convierte el pasado en un lugar al que regresas sin querer.',
        'Te refugias en tu caparazón cuando deberías permitirte ser visto.',
      ],
      nucleoTitle: 'El Núcleo Cáncer',
      nucleoDescription:
        'Una exploración del Guardián de las Mareas Internas. No es debilidad emocional. Es comprender por qué sientes tan profundo, por qué tu memoria emocional nunca desaparece y cómo transformar protección en conexión.',
      descubriras: [
        'Por qué guardas heridas que el resto del mundo ya olvidó',
        'Tu relación con el apego, la familia y la sensación de hogar',
        'El miedo al abandono que rige decisiones que crees racionales',
        'Tu lado sombrío: aislamiento, victimización emocional y manipulación afectiva',
        'Cómo amas cuando te permites confiar antes de proteger',
        'Cómo sanar sin tener que esconderte para hacerlo',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Cáncer.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Guardas memorias como si fueran tesoros, incluso las que te lastiman. Te escondes en tu caparazón antes de que alguien pueda herirte. No es debilidad — es un patrón de protección emocional profunda.',
      patronKeywords: ['memoria', 'protección', 'apego', 'vulnerabilidad'],
      desbloqueoItems: [
        'Comprender por qué te refugias en el pasado',
        'Identificar patrones de protección que suelen repetirse',
        'Explorar partes de tu sensibilidad que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de agua',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'leo',
    name: 'Leo',
    entidad: 'León solar divino',
    arquetipo: 'El Rey Solar',
    heroPromise:
      'No buscas atención. Buscas que alguien finalmente te vea como realmente eres.',
    colors: {
      primary: '#E6B800',
      secondary: '#1A1A1A',
      accent: '#FFC847',
    },
    particleType: 'fire',
    phrase: 'La presencia no se aprende. Se impone.',
    symbol: '♌',
    imageUrl: `${CDN_BASE}/05_LEO_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759432863838:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774846341214:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811165933662:1',
    content: {
      problemaTitle:
        'Brillas, pero también cargas el miedo a no ser amado por quien eres.',
      problemaSubtitle:
        'Y a veces actúas para que nadie descubra cuánto necesitas realmente ser visto.',
      problemas: [
        'Tu energía llena cualquier espacio, pero por dentro a veces te sientes invisible.',
        'Detrás de esa fuerza hay una necesidad profunda de ser reconocido sin tener que demostrar.',
        'Quieres ser visto no solo por lo que haces, sino por quien realmente eres.',
        'Tu orgullo te impide pedir ayuda incluso cuando la necesitas desesperadamente.',
      ],
      nucleoTitle: 'El Núcleo Leo',
      nucleoDescription:
        'Una exploración del Rey Solar. No es ego. Es comprender tu necesidad de expresión, reconocimiento y autenticidad sin depender de aprobación externa.',
      descubriras: [
        'Por qué necesitas ser visto y qué herida está debajo de esa necesidad',
        'La diferencia entre presencia auténtica y la actuación que ya no te calma',
        'El orgullo herido como mecanismo de defensa de tu vulnerabilidad',
        'Tu lado sombrío: dramatización, validación externa, miedo a ser ignorado',
        'Cómo amas cuando dejas de actuar para impresionar a quien ya te ama',
        'Cómo brillar sin tener que consumirte para sostener tu propia luz',
      ],
      previewNote: 'Vista previa de la experiencia inmersiva del Reino de Leo.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Proyectas una luz inmensa para que nadie note la sombra de tu propia duda. Buscas ser visto porque en el fondo temes no ser suficiente. No es ego — es un patrón de validación que aprendiste a actuar.',
      patronKeywords: ['orgullo', 'validación', 'actuación', 'presencia'],
      desbloqueoItems: [
        'Comprender por qué necesitas brillar constantemente',
        'Identificar patrones de orgullo que suelen repetirse',
        'Explorar partes de tu vulnerabilidad que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad solar',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'virgo',
    name: 'Virgo',
    entidad: 'Cierva/ave sagrada de mármol y luz',
    arquetipo: 'La Arquitecta del Orden',
    heroPromise:
      'No es perfeccionismo. Es el peso silencioso de creer que si bajas la guardia, todo se derrumba.',
    colors: {
      primary: '#D4D4C8',
      secondary: '#556B2F',
      accent: '#D4AF37',
    },
    particleType: 'light',
    phrase: 'Ve el caos oculto donde otros ven normalidad.',
    symbol: '♍',
    imageUrl: `${CDN_BASE}/06_VIRGO_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759433125982:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774846373982:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811168096350:1',
    content: {
      problemaTitle: 'Lo notas todo.',
      problemaSubtitle:
        'Y por eso casi nunca te permites descansar de tu propia mente.',
      problemas: [
        'Tu mente busca orden constantemente, incluso cuando deberías estar descansando.',
        'Notas detalles que nadie más ve y te cuesta soltarlos.',
        'Intentas corregir todo porque temes lo que pasaría si dejaras de hacerlo.',
        'Tu autoexigencia te impide reconocer cuánto ya has logrado.',
      ],
      nucleoTitle: 'El Núcleo Virgo',
      nucleoDescription:
        'Una exploración de la Arquitecta del Orden. No es perfeccionismo vacío. Es comprender por qué necesitas estructura y cómo encontrar equilibrio entre control y paz interior.',
      descubriras: [
        'Por qué tu mente analiza todo, incluso cuando intentas descansar',
        'La ansiedad disfrazada de "alta exigencia" y cómo desactivarla',
        'El miedo profundo a que algo se rompa si tú no lo sostienes todo',
        'Tu lado sombrío: crítica interna, control disfrazado de cuidado',
        'Cómo amas cuando bajas los estándares imposibles que te impones a ti mismo',
        'Cómo construir paz interior sin tener que perfeccionar primero el exterior',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Virgo.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Tu mente nunca descansa. Analizas, corriges, anticipas. Crees que es disciplina, pero hay un patrón debajo: un miedo silencioso a que algo se rompa si dejas de sostenerlo todo.',
      patronKeywords: [
        'control',
        'perfeccionismo',
        'sobreanálisis',
        'exigencia',
      ],
      desbloqueoItems: [
        'Comprender por qué necesitas ordenarlo todo',
        'Identificar patrones de autoexigencia que suelen repetirse',
        'Explorar partes de tu ansiedad que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad analítica',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'libra',
    name: 'Libra',
    entidad: 'Cisne/ave celestial de equilibrio',
    arquetipo: 'La Guardiana del Equilibrio',
    heroPromise:
      'No eres indeciso. Te perdiste tantas veces complaciendo a otros que ya no sabes qué quieres tú.',
    colors: {
      primary: '#E8C5C9',
      secondary: '#FFFAF0',
      accent: '#F0E68C',
    },
    particleType: 'air',
    phrase: 'Donde todo se rompe, Libra busca armonía.',
    symbol: '♎',
    imageUrl: `${CDN_BASE}/07LIBRA01_1.png?v=1778623066`,
    checkoutUrl: 'https://los12reinos.com/cart/42759433420894:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774847225950:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811168522334:1',
    content: {
      problemaTitle: 'Cuando todo está en paz afuera, algo se rompe adentro.',
      problemaSubtitle:
        'Y a veces te pierdes intentando sostener la armonía de todos menos la tuya.',
      problemas: [
        'Lees emociones y tensiones incluso cuando nadie las expresa.',
        'Quieres conexión profunda, pero temes decepcionar a los demás.',
        'Adaptarte se convirtió en un reflejo automático que ya no sabes cómo apagar.',
        'Te pierdes en lo que otros necesitan antes de saber qué necesitas tú.',
      ],
      nucleoTitle: 'El Núcleo Libra',
      nucleoDescription:
        'Una exploración de la Guardiana del Equilibrio. No es indecisión. Es comprender tu necesidad de armonía, belleza y conexión emocional auténtica.',
      descubriras: [
        'Por qué buscas equilibrio incluso a costa de tu propia voz',
        'La complacencia como estrategia de supervivencia emocional',
        'El miedo al conflicto que te lleva a desaparecer dentro de los demás',
        'Tu lado sombrío: evasión, dependencia afectiva, manipulación suave',
        'Cómo amas cuando dejas de perderte en lo que el otro necesita',
        'Cómo elegirte sin romper la armonía que tanto te importa',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Libra.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Ajustas tu forma de ser para mantener la paz de los demás, olvidando qué es lo que tú realmente quieres. Evitas el conflicto a toda costa. No es amabilidad — es un patrón de complacencia que te borra.',
      patronKeywords: ['complacencia', 'armonía', 'evasión', 'dependencia'],
      desbloqueoItems: [
        'Comprender por qué evitas el conflicto a toda costa',
        'Identificar patrones de complacencia que suelen repetirse',
        'Explorar partes de tu propia voz que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de equilibrio',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'escorpio',
    name: 'Escorpio',
    entidad: 'Escorpión abismal cósmico',
    arquetipo: 'El Guardián del Abismo',
    heroPromise:
      'No eres intenso. Sientes en una profundidad que la mayoría ni siquiera roza.',
    colors: {
      primary: '#6B2C91',
      secondary: '#0A0A0A',
      accent: '#B47BD8',
    },
    particleType: 'smoke',
    phrase: 'No teme a la oscuridad. Aprendió a vivir dentro de ella.',
    symbol: '♏',
    imageUrl: `${CDN_BASE}/08_ESCORPIO_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759433683038:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774850338910:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811168587870:1',
    content: {
      problemaTitle: 'Sientes en capas que pocos pueden sostener.',
      problemaSubtitle:
        'Y aprendiste a esconder esa intensidad para que nadie la use en tu contra.',
      problemas: [
        'Lees emociones que otros intentan ocultar y eso te aísla sin querer.',
        'Quieres conexión real, pero temes entregar completamente el control.',
        'Tu intensidad es tu poder, pero también tu refugio para no ser visto del todo.',
        'Aprendiste a vincularte desde la sospecha antes que desde la confianza.',
      ],
      nucleoTitle: 'El Núcleo Escorpio',
      nucleoDescription:
        'Una exploración del Guardián del Abismo. No es oscuridad destructiva. Es comprender por qué sientes tan profundo, por qué tu energía transforma todo lo que toca y cómo convertir intensidad en poder consciente.',
      descubriras: [
        'Por qué sientes a una profundidad que pocos pueden sostener',
        'La intensidad como armadura y como poder transformador',
        'El miedo a la vulnerabilidad que disfrazas de control',
        'Tu lado sombrío: obsesión, posesividad, venganza emocional silenciosa',
        'Cómo amas cuando dejas de defenderte de quien quiere acercarse',
        'Cómo transformar dolor profundo en poder consciente',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Escorpio.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Sientes todo con una intensidad que asusta, así que construyes armaduras de control y misterio. Te vinculas desde la sospecha para no ser traicionado. No es desconfianza — es un patrón de supervivencia emocional.',
      patronKeywords: ['intensidad', 'control', 'sospecha', 'profundidad'],
      desbloqueoItems: [
        'Comprender por qué sientes con tanta intensidad',
        'Identificar patrones de control que suelen repetirse',
        'Explorar partes de tu sombra que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad abismal',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'sagitario',
    name: 'Sagitario',
    entidad: 'Centauro/cierva-fénix astral',
    arquetipo: 'El Arquero del Horizonte',
    heroPromise:
      'No estás huyendo. Te mueves antes de que algo te ate a algo que no elegiste.',
    colors: {
      primary: '#3D5AC9',
      secondary: '#FF6347',
      accent: '#FFD700',
    },
    particleType: 'fire',
    phrase: 'No nació para quedarse. Nació para buscar.',
    symbol: '♐',
    imageUrl: `${CDN_BASE}/09_SAGITARIO_1.png`,
    checkoutUrl: 'https://los12reinos.com/cart/42759433945182:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774850502750:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811168850014:1',
    content: {
      problemaTitle: 'Necesitas moverte.',
      problemaSubtitle:
        'Y a veces eso se confunde con huir de lo que realmente importa.',
      problemas: [
        'Tu alma necesita movimiento incluso cuando el momento pedía quedarte.',
        'Buscas significado constantemente y nunca te alcanza con lo encontrado.',
        'Temes sentirte atrapado emocionalmente o perder tu libertad.',
        'Confundes huir con buscar cuando algo se vuelve demasiado real.',
      ],
      nucleoTitle: 'El Núcleo Sagitario',
      nucleoDescription:
        'Una exploración del Arquero del Horizonte. No es irresponsabilidad. Es comprender tu necesidad de exploración, verdad y expansión emocional.',
      descubriras: [
        'Por qué tu cuerpo necesita movimiento cuando tu emoción se vuelve densa',
        'La diferencia entre buscar y huir cuando algo te toca de verdad',
        'El miedo a quedarte atrapado que dirige decisiones que crees libres',
        'Tu lado sombrío: evasión emocional, exceso de independencia, distancia',
        'Cómo amas cuando decides quedarte aún cuando podrías irte',
        'Cómo encontrar libertad sin tener que huir para sentirla',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Sagitario.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Cuando algo se vuelve demasiado real o denso, tu primer instinto es buscar la salida. Llenas tu vida de movimiento para no tener que quedarte quieto. No es libertad — es un patrón de huida.',
      patronKeywords: ['huida', 'movimiento', 'evasión', 'búsqueda'],
      desbloqueoItems: [
        'Comprender por qué necesitas escapar constantemente',
        'Identificar patrones de huida que suelen repetirse',
        'Explorar partes de tu densidad emocional que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de expansión',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'capricornio',
    name: 'Capricornio',
    entidad: 'Cabra titánica de montaña y obsidiana',
    arquetipo: 'El Titán de la Cima',
    heroPromise:
      'No es disciplina. Es el miedo silencioso a no valer si dejas de producir.',
    colors: {
      primary: '#5C6B73',
      secondary: '#C0C0C0',
      accent: '#A8B5BD',
    },
    particleType: 'earth',
    phrase: 'Mientras otros sueñan, Capricornio construye.',
    symbol: '♑',
    imageUrl: `${CDN_BASE}/010CAPRICORNIO_1.png?v=1778623139`,
    checkoutUrl: 'https://los12reinos.com/cart/42759434240094:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774850732126:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811168915550:1',
    content: {
      problemaTitle: 'Sostienes todo. Incluso cuando nadie te sostiene a ti.',
      problemaSubtitle:
        'Y por dentro cargas un cansancio que nunca te permitiste mostrar.',
      problemas: [
        'Tu energía resiste incluso cuando todo se derrumba — y eso también te aísla.',
        'Te exiges constantemente para no sentir que eres prescindible.',
        'Quieres control porque temes fracasar frente a quienes esperan algo de ti.',
        'Tu fortaleza es real, pero también es el muro que no deja entrar a nadie.',
      ],
      nucleoTitle: 'El Núcleo Capricornio',
      nucleoDescription:
        'Una exploración del Titán de la Cima. No es frialdad. Es comprender tu necesidad de estructura, logro y control emocional.',
      descubriras: [
        'Por qué tu valor se sintió siempre ligado a lo que produces',
        'La diferencia entre disciplina sana y autoexigencia que te agota',
        'El miedo al fracaso que dirige tu vida sin que lo nombres',
        'Tu lado sombrío: rigidez, frialdad emocional, control disfrazado de responsabilidad',
        'Cómo amas cuando permites que alguien te vea cansado, no solo fuerte',
        'Cómo construir sin destruirte en el proceso',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Capricornio.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Cargas el peso del mundo en silencio porque aprendiste que tu valor depende de lo que produces. Te aíslas en tu propia fortaleza. No es frialdad — es un patrón de autoexigencia que no te deja caer.',
      patronKeywords: ['peso', 'logro', 'aislamiento', 'estructura'],
      desbloqueoItems: [
        'Comprender por qué mides tu valor en logros',
        'Identificar patrones de aislamiento que suelen repetirse',
        'Explorar partes de tu cansancio que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad de cima',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'acuario',
    name: 'Acuario',
    entidad: 'Criatura aérea portadora de agua cósmica',
    arquetipo: 'El Rebelde del Cielo',
    heroPromise:
      'No eres frío. Aprendiste muy pronto que era más seguro pensar que sentir.',
    colors: {
      primary: '#1FA8DC',
      secondary: '#00CED1',
      accent: '#9370DB',
    },
    particleType: 'electric',
    phrase: 'No vino a encajar. Vino a alterar el sistema.',
    symbol: '♒',
    imageUrl: `${CDN_BASE}/011ACUARIO_1.png?v=1778623164`,
    checkoutUrl: 'https://los12reinos.com/cart/42759435026526:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774850764894:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811169243230:1',
    content: {
      problemaTitle: 'Sientes que ves el mundo desde afuera.',
      problemaSubtitle:
        'Y a veces te aíslas para no perder la libertad de pensar distinto.',
      problemas: [
        'Tu mente vive adelantada al presente y eso te separa de quienes están aquí.',
        'Observas el mundo desde afuera incluso cuando estás dentro.',
        'Quieres conexión real, pero temes perder tu libertad mental.',
        'Tu independencia es tu fortaleza y también la distancia que no logras cerrar.',
      ],
      nucleoTitle: 'El Núcleo Acuario',
      nucleoDescription:
        'Una exploración del Rebelde del Cielo. No es frialdad. Es comprender tu necesidad de independencia, innovación y autenticidad emocional.',
      descubriras: [
        'Por qué piensas más rápido de lo que puedes sentir',
        'La hiper independencia como forma sutil de evitar vínculos profundos',
        'El miedo a perder tu individualidad si te acercas demasiado a alguien',
        'Tu lado sombrío: desconexión emocional, rebeldía defensiva, frialdad táctica',
        'Cómo amas cuando permites cercanía sin sentir que pierdes libertad',
        'Cómo conectar sin perderte y diferenciarte sin aislarte',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Acuario.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Observas tus propias emociones desde afuera, como si le pasaran a otra persona. Mantienes distancia para asegurar tu independencia. No es desapego — es un patrón para proteger tu vulnerabilidad.',
      patronKeywords: ['distancia', 'desapego', 'independencia', 'mente'],
      desbloqueoItems: [
        'Comprender por qué te desconectas de lo que sientes',
        'Identificar patrones de distancia que suelen repetirse',
        'Explorar partes de tu vulnerabilidad que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad rebelde',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
  {
    id: 'piscis',
    name: 'Piscis',
    entidad: 'Peces cósmicos / dragón acuático astral',
    arquetipo: 'El Guardián del Mar Astral',
    heroPromise:
      'No exageras tus emociones. Absorbes las del mundo y nadie te enseñó cómo soltarlas.',
    colors: {
      primary: '#4682B4',
      secondary: '#40E0D0',
      accent: '#B19CD9',
    },
    particleType: 'water',
    phrase: 'Hay mundos que solo existen dentro de Piscis.',
    symbol: '♓',
    imageUrl: `${CDN_BASE}/012PISCIS.png?v=1778623410`,
    checkoutUrl: 'https://los12reinos.com/cart/42759436075102:1',
    checkoutUrlCompleta: 'https://los12reinos.com/cart/42774853845086:1',
    checkoutUrlUpsell: 'https://los12reinos.com/cart/42811169308766:1',
    content: {
      problemaTitle: 'Sientes lo que otros ni siquiera nombran.',
      problemaSubtitle:
        'Y por proteger ese mundo interno, a veces te disuelves dentro de él.',
      problemas: [
        'Tu energía es profundamente intuitiva y absorbe lo que está a tu alrededor.',
        'Sientes emociones incluso cuando nadie las expresa, y no siempre sabes si son tuyas.',
        'Quieres salvar, sanar y conectar… incluso cuando eso te destruye.',
        'Te disuelves en los demás antes de recordar dónde terminas tú.',
      ],
      nucleoTitle: 'El Núcleo Piscis',
      nucleoDescription:
        'Una exploración del Guardián del Mar Astral. No es debilidad. Es comprender tu sensibilidad extrema, tu intuición y tu conexión con lo invisible.',
      descubriras: [
        'Por qué absorbes emociones que ni siquiera son tuyas',
        'La diferencia entre intuición y angustia heredada del entorno',
        'El escapismo como forma de proteger un mundo interno que sientes frágil',
        'Tu lado sombrío: idealización, evasión, agotamiento emocional crónico',
        'Cómo amas cuando aprendes a no desaparecer para sostener al otro',
        'Cómo sentir profundo sin ahogarte en lo que sientes',
      ],
      previewNote:
        'Vista previa de la experiencia inmersiva del Reino de Piscis.',
      patronTitle: 'El Patrón Que Se Repite',
      patronSubtitle:
        'Tal vez no estás perdido. Tal vez estás repitiendo un patrón.',
      patronText:
        'Sientes lo que otros ni siquiera nombran. Absorbes emociones ajenas hasta confundirlas con las tuyas. No es debilidad — es un patrón de entrega que nadie te enseñó a soltar.',
      patronKeywords: ['sensibilidad', 'desconexión', 'absorción', 'entrega'],
      desbloqueoItems: [
        'Comprender por qué absorbes las emociones de otros',
        'Identificar patrones de disolución que suelen repetirse',
        'Explorar partes de tu mundo interno que normalmente ignoras',
        'Acceder a una experiencia inmersiva basada en tu identidad astral',
        'Explorar tu Reino interior desde una nueva perspectiva',
        'Acceso privado desde cualquier lugar',
        'Una experiencia diseñada para sentirse personal',
      ],
      paraQuienItems: BASE_PARA_QUIEN,
    },
  },
]

export const HERO_BACKGROUND_IMAGE = `${CDN_BASE}/00_LOS_12_1_c39dfca6-b1ea-480d-b79c-201898dbd509.png?v=1778618969`

export const HERO_BACKGROUND_IMAGE_MOBILE = `${CDN_BASE}/ChatGPT_Image_12_may_2026_08_45_23_p.m._1_1.png?v=1778633220`

export const getSignById = (id: string): Sign | undefined => {
  return signs.find((sign) => sign.id === id)
}
